import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";

import Loader from "../components/Loader";
import { generateCertificate } from "../utils/certificateGenerator";

const ActionButtonStyle = () => (
    <style>{`
        .action-btn {
            width: 32px;
            height: 32px;
            padding: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            transition: all 0.2s ease;
        }
        .action-btn i {
            font-size: 0.9rem;
        }
    `}</style>
);

const TRAINING_COL = "TRAINING_CATALOG";
const ASSIGN_COL = "TRAINING_ASSIGNMENTS";
const WELFARE_COL = "WELFARE_LOGS";

export default function TrainingWelfare() {
    const [activeTab, setActiveTab] = useState("CATALOG");
    const [catalog, setCatalog] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [welfareLogs, setWelfareLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showWelfareModal, setShowWelfareModal] = useState(false);
    const [selectedTraining, setSelectedTraining] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const [form, setForm] = useState({
        name: "",
        category: "Technical",
        description: "",
        trainer: "",
        mode: "Offline",
        duration: "",
        materialUrl: "",
        isMandatory: false,
    });

    const [assignForm, setAssignForm] = useState({
        trainingId: "",
        employeeId: "",
        scheduledDate: "",
        notes: "",
    });

    const [welfareForm, setWelfareForm] = useState({ employeeId: "", type: "PPE Issuance", details: "", date: new Date().toISOString().split('T')[0] });

    useEffect(() => {
        if (activeTab === "CATALOG") fetchCatalog();
        if (activeTab === "ASSIGNMENT" || activeTab === "REPORTS") {
            fetchAssignments();
            fetchEmployees();
            if (activeTab === "REPORTS") fetchCatalog();
        }
        if (activeTab === "WELFARE") {
            fetchWelfareLogs();
            fetchEmployees();
        }
    }, [activeTab]);

    const fetchCatalog = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, TRAINING_COL), orderBy("name"));
            const snap = await getDocs(q);
            setCatalog(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error(err);
            toast.error("Failed to load catalog");
        } finally {
            setLoading(false);
        }
    };

    const fetchAssignments = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, ASSIGN_COL), orderBy("scheduledDate", "desc"));
            const snap = await getDocs(q);
            setAssignments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error(err);
            toast.error("Failed to load assignments");
        } finally {
            setLoading(false);
        }
    };

    const fetchWelfareLogs = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, WELFARE_COL), orderBy("date", "desc"));
            const snap = await getDocs(q);
            setWelfareLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error(err);
            toast.error("Failed to load welfare logs");
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployees = async () => {
        try {
            const snap = await getDocs(collection(db, "EMPLOYEES"));
            setEmployees(snap.docs.map(d => ({ id: d.id, name: d.data().name })));
        } catch (err) {
            console.error(err);
        }
    };

    const handleSaveTraining = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            if (form.id) {
                const ref = doc(db, TRAINING_COL, form.id);
                const { id, ...dataToUpdate } = form;
                await updateDoc(ref, {
                    ...dataToUpdate,
                    updatedAt: new Date(),
                });
                toast.success("Training program updated!");
            } else {
                await addDoc(collection(db, TRAINING_COL), {
                    ...form,
                    createdAt: new Date(),
                });
                toast.success("Training program created!");
            }
            setShowModal(false);
            setForm({ name: "", category: "Technical", description: "", trainer: "", mode: "Offline", duration: "", materialUrl: "", isMandatory: false });
            fetchCatalog();
        } catch (err) {
            console.error(err);
            toast.error("Failed to save training");
        } finally {
            setLoading(false);
        }
    };

    const handleEditTraining = (item) => {
        setForm({ ...item });
        setShowDetailsModal(false);
        setShowModal(true);
    };

    const handleDeleteTraining = async (id) => {
        if (!window.confirm("Are you sure you want to delete this training program?")) return;
        try {
            setLoading(true);
            await deleteDoc(doc(db, TRAINING_COL, id));
            toast.success("Training deleted successfully");
            setShowDetailsModal(false);
            fetchCatalog();
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete training");
        } finally {
            setLoading(false);
        }
    };

    const handleAssignTraining = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const training = catalog.find(t => t.id === assignForm.trainingId);
            const employee = employees.find(e => e.id === assignForm.employeeId);

            if (assignForm.id) {
                const ref = doc(db, ASSIGN_COL, assignForm.id);
                const { id, ...dataToUpdate } = assignForm;
                await updateDoc(ref, {
                    ...dataToUpdate,
                    trainingName: training?.name || "",
                    employeeName: employee?.name || "",
                    updatedAt: new Date(),
                });
                toast.success("Assignment updated!");
            } else {
                await addDoc(collection(db, ASSIGN_COL), {
                    ...assignForm,
                    trainingName: training?.name || "",
                    employeeName: employee?.name || "",
                    status: "Scheduled",
                    assignedAt: new Date(),
                });
                toast.success("Training assigned successfully!");
            }
            setShowAssignModal(false);
            setAssignForm({ trainingId: "", employeeId: "", scheduledDate: "", notes: "" });
            fetchAssignments();
        } catch (err) {
            console.error(err);
            toast.error("Failed to save assignment");
        } finally {
            setLoading(false);
        }
    };

    const handleEditAssignment = (item) => {
        setAssignForm({ ...item });
        setShowAssignModal(true);
    };

    const handleDeleteAssignment = async (id) => {
        if (!window.confirm("Are you sure you want to delete this assignment?")) return;
        try {
            setLoading(true);
            await deleteDoc(doc(db, ASSIGN_COL, id));
            toast.success("Assignment deleted");
            fetchAssignments();
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete assignment");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveWelfare = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const employee = employees.find(e => e.id === welfareForm.employeeId);

            if (welfareForm.id) {
                const ref = doc(db, WELFARE_COL, welfareForm.id);
                const { id, ...dataToUpdate } = welfareForm;
                await updateDoc(ref, {
                    ...dataToUpdate,
                    employeeName: employee?.name || "",
                    updatedAt: new Date(),
                });
                toast.success("Welfare record updated!");
            } else {
                await addDoc(collection(db, WELFARE_COL), {
                    ...welfareForm,
                    employeeName: employee?.name || "",
                    createdAt: new Date(),
                });
                toast.success("Welfare record saved!");
            }
            setShowWelfareModal(false);
            setWelfareForm({ employeeId: "", type: "PPE Issuance", details: "", date: new Date().toISOString().split('T')[0] });
            fetchWelfareLogs();
        } catch (err) {
            console.error(err);
            toast.error("Failed to save welfare record");
        } finally {
            setLoading(false);
        }
    };

    const handleEditWelfare = (item) => {
        setWelfareForm({ ...item });
        setShowWelfareModal(true);
    };

    const handleDeleteWelfare = async (id) => {
        if (!window.confirm("Are you sure you want to delete this record?")) return;
        try {
            setLoading(true);
            await deleteDoc(doc(db, WELFARE_COL, id));
            toast.success("Record deleted");
            fetchWelfareLogs();
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete record");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
        try {
            setLoading(true);
            const ref = doc(db, ASSIGN_COL, id);
            await updateDoc(ref, { status: newStatus, updatedAt: new Date() });
            toast.success(`Status updated to ${newStatus}`);
            fetchAssignments();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update status");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-3 text-center">
            <ActionButtonStyle />
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold mb-0">Training & Welfare Management</h4>
                <div className="d-flex gap-2">
                    {activeTab === "CATALOG" && (
                        <button className="btn btn-primary btn-sm shadow-sm" onClick={() => { setForm({ name: "", category: "Technical", description: "", trainer: "", mode: "Offline", duration: "", materialUrl: "", isMandatory: false }); setShowModal(true); }}>
                            <i className="bi bi-plus-lg me-1"></i> Create Training
                        </button>
                    )}
                    {activeTab === "ASSIGNMENT" && (
                        <button className="btn btn-success btn-sm shadow-sm" onClick={() => { setAssignForm({ trainingId: "", employeeId: "", scheduledDate: "", notes: "" }); setShowAssignModal(true); }}>
                            <i className="bi bi-plus-lg me-1"></i> Assign Training
                        </button>
                    )}
                </div>
            </div>

            {/* Navigation Tabs with Counts */}
            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'CATALOG' ? 'active fw-bold' : 'text-muted'}`} onClick={() => setActiveTab('CATALOG')}>
                        Training Catalog ({catalog.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'ASSIGNMENT' ? 'active fw-bold' : 'text-muted'}`} onClick={() => setActiveTab('ASSIGNMENT')}>
                        Assignments ({assignments.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'REPORTS' ? 'active fw-bold' : 'text-muted'}`} onClick={() => setActiveTab('REPORTS')}>
                        {(() => {
                            const mandatoryTrainings = catalog.filter(t => t.isMandatory);
                            const gaps = mandatoryTrainings.filter(t => {
                                const assignedToEmpCount = assignments.filter(a => a.trainingId === t.id && a.status === 'Completed').length;
                                return assignedToEmpCount < employees.length;
                            });
                            return `Performance Reports (${gaps.length})`;
                        })()}
                    </button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'WELFARE' ? 'active fw-bold' : 'text-muted'}`} onClick={() => setActiveTab('WELFARE')}>
                        Welfare & Safety ({welfareLogs.length})
                    </button>
                </li>
            </ul>

            <div className="card border-0 shadow-sm min-vh-50">
                <div className="card-body p-4">
                    {loading && <Loader />}
                    {!loading && activeTab === "CATALOG" && (
                        <div>
                            {catalog.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-journal-bookmark display-1 text-light mb-3"></i>
                                    <h5>Training Catalog is Empty</h5>
                                    <p className="text-muted">Start by defining your first technical or safety training program.</p>
                                </div>
                            ) : (
                                <div className="row g-3 text-start">
                                    {catalog.map(item => (
                                        <div className="col-md-4" key={item.id}>
                                            <div className="card h-100 border-0 bg-light shadow-sm p-3">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <span className={`badge ${item.category === 'Technical' ? 'bg-info text-dark' : item.category === 'Safety' ? 'bg-danger' : 'bg-primary'}`}>
                                                        {item.category}
                                                    </span>
                                                    {item.isMandatory && <span className="badge rounded-pill bg-warning text-dark small">Mandatory</span>}
                                                </div>
                                                <h6 className="fw-bold mb-1">{item.name}</h6>
                                                <p className="small text-muted mb-3 flex-grow-1">{item.description}</p>
                                                <hr className="my-2 opacity-10" />
                                                <div className="d-flex justify-content-between align-items-center mt-auto">
                                                    <div className="small text-muted">
                                                        <i className="bi bi-clock me-1"></i>{item.duration || "N/A"}
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button className="action-btn btn btn-outline-primary" title="View" onClick={() => { setSelectedTraining(item); setShowDetailsModal(true); }}>
                                                            <i className="bi bi-eye"></i>
                                                        </button>
                                                        <button className="action-btn btn btn-outline-warning" title="Edit" onClick={() => handleEditTraining(item)}>
                                                            <i className="bi bi-pencil"></i>
                                                        </button>
                                                        <button className="action-btn btn btn-outline-danger" title="Delete" onClick={() => handleDeleteTraining(item.id)}>
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {!loading && activeTab === "ASSIGNMENT" && (
                        <div>
                            {assignments.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-person-check display-1 text-light mb-3"></i>
                                    <h5>No Training Assignments</h5>
                                    <p className="text-muted">You haven't assigned any training programs to employees yet.</p>
                                    <button className="btn btn-success btn-sm mt-3" onClick={() => { setAssignForm({ trainingId: "", employeeId: "", scheduledDate: "", notes: "" }); setShowAssignModal(true); }}>Assign Now</button>
                                </div>
                            ) : (
                                <div className="table-responsive text-start">
                                    <table className="table table-hover align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Employee</th>
                                                <th>Training</th>
                                                <th>Scheduled</th>
                                                <th>Status</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignments.map(a => (
                                                <tr key={a.id}>
                                                    <td className="fw-bold">{a.employeeName}</td>
                                                    <td>{a.trainingName}</td>
                                                    <td>{a.scheduledDate}</td>
                                                    <td>
                                                        <select
                                                            className={`form-select form-select-sm fw-bold border-0 bg-${a.status === "Completed" ? "success-subtle text-success" : (a.status === "Failed" ? "danger-subtle text-danger" : (a.status === "Cancelled" ? "secondary-subtle text-dark" : "warning-subtle text-dark"))}`}
                                                            style={{ width: "fit-content" }}
                                                            value={a.status || "Scheduled"}
                                                            onChange={(e) => handleUpdateStatus(a.id, e.target.value)}
                                                        >
                                                            <option value="Scheduled">Scheduled</option>
                                                            <option value="Completed">Completed</option>
                                                            <option value="Failed">Failed</option>
                                                            <option value="Cancelled">Cancelled</option>
                                                        </select>
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="d-flex gap-1 justify-content-end">
                                                            {a.status === 'Completed' && (
                                                                <button
                                                                    className="action-btn btn btn-outline-success"
                                                                    onClick={() => generateCertificate(a)}
                                                                    title="Download Certificate"
                                                                >
                                                                    <i className="bi bi-file-earmark-pdf"></i>
                                                                </button>
                                                            )}
                                                            <button className="action-btn btn btn-outline-warning" onClick={() => handleEditAssignment(a)} title="Edit">
                                                                <i className="bi bi-pencil"></i>
                                                            </button>
                                                            <button className="action-btn btn btn-outline-danger" onClick={() => handleDeleteAssignment(a.id)} title="Delete">
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {!loading && activeTab === "REPORTS" && (
                        <div>
                            {(() => {
                                const completedCount = assignments.filter(a => a.status === 'Completed').length;
                                const totalAssigned = assignments.length || 1;
                                const completionRate = Math.round((completedCount / totalAssigned) * 100);

                                const mandatoryTrainings = catalog.filter(t => t.isMandatory);
                                const gaps = mandatoryTrainings.filter(t => {
                                    const assignedToEmpCount = assignments.filter(a => a.trainingId === t.id && a.status === 'Completed').length;
                                    return assignedToEmpCount < employees.length;
                                });

                                return (
                                    <div className="py-2 text-start">
                                        <div className="row g-4 mb-5">
                                            <div className="col-md-4">
                                                <div className="card p-4 border-0 bg-primary text-white shadow-sm h-100">
                                                    <h1 className="display-4 fw-bold">{completionRate}%</h1>
                                                    <p className="fw-bold text-uppercase small opacity-75">Overall Completion Rate</p>
                                                    <div className="progress bg-white bg-opacity-25" style={{ height: '8px' }}>
                                                        <div className="progress-bar bg-white" role="progressbar" style={{ width: `${completionRate}%` }}></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="card p-4 border-0 bg-success text-white shadow-sm h-100">
                                                    <h1 className="display-4 fw-bold">{completedCount}</h1>
                                                    <p className="fw-bold text-uppercase small opacity-75">Valid Certifications</p>
                                                    <p className="small mb-0 opacity-75">Across {employees.length} employees</p>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="card p-4 border-0 bg-warning text-dark shadow-sm h-100">
                                                    <h1 className="display-4 fw-bold">{gaps.length}</h1>
                                                    <p className="fw-bold text-uppercase small opacity-75">Critical Skill Gaps</p>
                                                    <p className="small mb-0 opacity-75">Mandatory courses not completed by all</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-7">
                                                <h5 className="fw-bold mb-3"><i className="bi bi-award me-2 text-primary"></i>Top Performing Skills</h5>
                                                <div className="list-group list-group-flush border rounded overflow-hidden">
                                                    {catalog.slice(0, 5).map(t => {
                                                        const count = assignments.filter(a => a.trainingId === t.id && a.status === 'Completed').length;
                                                        const perc = Math.round((count / (employees.length || 1)) * 100);
                                                        return (
                                                            <div className="list-group-item d-flex justify-content-between align-items-center py-3" key={t.id}>
                                                                <div className="flex-grow-1 me-3">
                                                                    <div className="fw-bold">{t.name}</div>
                                                                    <div className="progress mt-1" style={{ height: '4px' }}>
                                                                        <div className={`progress-bar ${perc > 70 ? 'bg-success' : perc > 30 ? 'bg-info' : 'bg-warning'}`} style={{ width: `${perc}%` }}></div>
                                                                    </div>
                                                                </div>
                                                                <span className="badge bg-light text-dark border">{count} Certs</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <div className="col-md-5">
                                                <h5 className="fw-bold mb-3"><i className="bi bi-exclamation-octagon me-2 text-danger"></i>Urgent Compliance Needs</h5>
                                                {gaps.length === 0 ? (
                                                    <div className="alert alert-success border-0 shadow-sm">
                                                        <i className="bi bi-check-circle-fill me-2"></i> All mandatory trainings are up to date!
                                                    </div>
                                                ) : (
                                                    <div className="list-group list-group-flush border rounded overflow-hidden">
                                                        {gaps.map(g => (
                                                            <div className="list-group-item d-flex align-items-center py-3 border-start border-4 border-danger" key={g.id}>
                                                                <div className="flex-grow-1">
                                                                    <div className="fw-bold">{g.name}</div>
                                                                    <div className="small text-danger">Mandatory: Needs Attention</div>
                                                                </div>
                                                                <button className="btn btn-sm btn-outline-danger" onClick={() => setActiveTab("ASSIGNMENT")}>Assign</button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {!loading && activeTab === "WELFARE" && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5>Welfare & Safety Records</h5>
                                <button className="btn btn-primary btn-sm" onClick={() => { setWelfareForm({ employeeId: "", type: "PPE Issuance", details: "", date: new Date().toISOString().split('T')[0] }); setShowWelfareModal(true); }}>
                                    <i className="bi bi-plus-lg me-1"></i> Add Record
                                </button>
                            </div>
                            {welfareLogs.length === 0 ? (
                                <div className="text-center py-5 bg-light rounded">
                                    <i className="bi bi-shield-check display-1 text-light mb-3"></i>
                                    <h5>No Welfare Records</h5>
                                    <p className="text-muted">No PPE issuance or incident reports found.</p>
                                </div>
                            ) : (
                                <div className="table-responsive text-start">
                                    <table className="table table-hover align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Date</th>
                                                <th>Employee</th>
                                                <th>Type</th>
                                                <th>Details</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {welfareLogs.map(log => (
                                                <tr key={log.id}>
                                                    <td>{log.date}</td>
                                                    <td className="fw-bold">{log.employeeName}</td>
                                                    <td>
                                                        <span className={`badge ${log.type === 'Incident Report' ? 'bg-danger' : 'bg-info text-dark'}`}>
                                                            {log.type}
                                                        </span>
                                                    </td>
                                                    <td className="small">{log.details}</td>
                                                    <td className="text-end">
                                                        <div className="d-flex gap-1 justify-content-end">
                                                            <button className="action-btn btn btn-outline-warning" onClick={() => handleEditWelfare(log)} title="Edit">
                                                                <i className="bi bi-pencil"></i>
                                                            </button>
                                                            <button className="action-btn btn btn-outline-danger" onClick={() => handleDeleteWelfare(log.id)} title="Delete">
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            {showWelfareModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-md modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title fw-bold">{welfareForm.id ? "Edit Welfare / Safety Record" : "Add Welfare / Safety Record"}</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowWelfareModal(false)}></button>
                            </div>
                            <form onSubmit={handleSaveWelfare}>
                                <div className="modal-body p-4">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Select Employee</label>
                                        <select className="form-select" required value={welfareForm.employeeId} onChange={e => setWelfareForm({ ...welfareForm, employeeId: e.target.value })}>
                                            <option value="">-- Select Employee --</option>
                                            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Record Type</label>
                                        <select className="form-select" value={welfareForm.type} onChange={e => setWelfareForm({ ...welfareForm, type: e.target.value })}>
                                            <option value="PPE Issuance">PPE Issuance</option>
                                            <option value="Incident Report">Incident Report</option>
                                            <option value="Safety Training">Safety Briefing</option>
                                            <option value="Health Checkup">Health Checkup</option>
                                            <option value="Insurance Claim">Insurance Claim</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Occurance Date</label>
                                        <input type="date" className="form-control" required value={welfareForm.date} onChange={e => setWelfareForm({ ...welfareForm, date: e.target.value })} />
                                    </div>
                                    <div className="">
                                        <label className="form-label fw-bold">Details / Remarks</label>
                                        <textarea className="form-control" rows="3" required value={welfareForm.details} onChange={e => setWelfareForm({ ...welfareForm, details: e.target.value })} placeholder="Describe the issuance items or incident details..."></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer bg-light">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowWelfareModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                                        {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : "Save Record"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showAssignModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-md modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header bg-success text-white">
                                <h5 className="modal-title fw-bold">{assignForm.id ? "Edit Assignment" : "Assign Training Program"}</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowAssignModal(false)}></button>
                            </div>
                            <form onSubmit={handleAssignTraining}>
                                <div className="modal-body p-4">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Select Training</label>
                                        <select className="form-select" required value={assignForm.trainingId} onChange={e => setAssignForm({ ...assignForm, trainingId: e.target.value })}>
                                            <option value="">-- Select Training --</option>
                                            {catalog.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category})</option>)}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Select Employee</label>
                                        <select className="form-select" required value={assignForm.employeeId} onChange={e => setAssignForm({ ...assignForm, employeeId: e.target.value })}>
                                            <option value="">-- Select Employee --</option>
                                            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Scheduled Date</label>
                                        <input type="date" className="form-control" required value={assignForm.scheduledDate} onChange={e => setAssignForm({ ...assignForm, scheduledDate: e.target.value })} />
                                    </div>
                                    <div className="">
                                        <label className="form-label fw-bold">Special Notes</label>
                                        <textarea className="form-control" rows="2" value={assignForm.notes} onChange={e => setAssignForm({ ...assignForm, notes: e.target.value })} placeholder="Any specific instructions for this session..."></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer bg-light">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-success px-4" disabled={loading}>
                                        {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : "Save Assignment"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title fw-bold">{form.id ? "Edit Training Program" : "Create New Training Program"}</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={handleSaveTraining}>
                                <div className="modal-body p-4">
                                    <div className="row g-3">
                                        <div className="col-md-8">
                                            <label className="form-label fw-bold">Training Name</label>
                                            <input type="text" className="form-control" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Inverter AC Installation" />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold">Category</label>
                                            <select className="form-select" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                                                <option value="Technical">Technical</option>
                                                <option value="Safety">Safety</option>
                                                <option value="Soft Skills">Soft Skills</option>
                                                <option value="Compliance">Compliance</option>
                                            </select>
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-bold">Description</label>
                                            <textarea className="form-control" rows="2" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Provide a brief overview of the training..."></textarea>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">Trainer</label>
                                            <input type="text" className="form-control" value={form.trainer} onChange={e => setForm({ ...form, trainer: e.target.value })} placeholder="Internal or External Trainer" />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label fw-bold">Mode</label>
                                            <select className="form-select" value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
                                                <option value="Offline">Offline</option>
                                                <option value="Online">Online</option>
                                                <option value="On-Job">On-Job</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label fw-bold">Duration</label>
                                            <input type="text" className="form-control" value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} placeholder="e.g. 4 Hours" />
                                        </div>
                                        <div className="col-md-8">
                                            <label className="form-label fw-bold">Training Material (Link/URL)</label>
                                            <input type="url" className="form-control" value={form.materialUrl} onChange={e => setForm({ ...form, materialUrl: e.target.value })} placeholder="Link to PDF, Video or PPT" />
                                        </div>
                                        <div className="col-md-4 d-flex align-items-end">
                                            <div className="form-check mb-2">
                                                <input className="form-check-input" type="checkbox" id="mandatoryCheck" checked={form.isMandatory} onChange={e => setForm({ ...form, isMandatory: e.target.checked })} />
                                                <label className="form-check-label fw-bold" htmlFor="mandatoryCheck">
                                                    Mandatory Training
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer bg-light">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                                        {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : "Save Training"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showDetailsModal && selectedTraining && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title fw-bold">Training Details</h5>
                                <button type="button" className="btn-close" onClick={() => setShowDetailsModal(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="mb-4">
                                    <span className={`badge mb-2 ${selectedTraining.category === 'Technical' ? 'bg-info text-dark' : selectedTraining.category === 'Safety' ? 'bg-danger' : 'bg-primary'}`}>
                                        {selectedTraining.category}
                                    </span>
                                    {selectedTraining.isMandatory && <span className="badge rounded-pill bg-warning text-dark small ms-2">Mandatory</span>}
                                    <h4 className="fw-bold">{selectedTraining.name}</h4>
                                    <p className="text-muted">{selectedTraining.description || "No description provided."}</p>
                                </div>

                                <div className="row g-3 mb-4">
                                    <div className="col-6">
                                        <label className="text-muted small d-block mb-1">Trainer</label>
                                        <div className="fw-bold"><i className="bi bi-person me-2"></i>{selectedTraining.trainer || "Not assigned"}</div>
                                    </div>
                                    <div className="col-6">
                                        <label className="text-muted small d-block mb-1">Duration</label>
                                        <div className="fw-bold"><i className="bi bi-clock me-2"></i>{selectedTraining.duration || "N/A"}</div>
                                    </div>
                                    <div className="col-6">
                                        <label className="text-muted small d-block mb-1">Mode</label>
                                        <div className="fw-bold"><i className="bi bi-display me-2"></i>{selectedTraining.mode}</div>
                                    </div>
                                    {selectedTraining.materialUrl && (
                                        <div className="col-12">
                                            <label className="text-muted small d-block mb-1">Materials</label>
                                            <a href={selectedTraining.materialUrl} target="_blank" rel="noopener noreferrer" className="btn btn-light btn-sm text-primary w-100 text-start overflow-hidden">
                                                <i className="bi bi-link-45deg me-2"></i>{selectedTraining.materialUrl}
                                            </a>
                                        </div>
                                    )}
                                </div>

                                <div className="d-flex gap-2">
                                    <button className="btn btn-primary flex-grow-1" onClick={() => handleEditTraining(selectedTraining)}>
                                        <i className="bi bi-pencil me-2"></i>Edit
                                    </button>
                                    <button className="btn btn-outline-danger" onClick={() => handleDeleteTraining(selectedTraining.id)}>
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
